import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import SEO from "../../components/base/SEO";
import Footer from "../../components/feature/Footer";
import Header from "../../components/feature/Header";

const vendorCategories = [
  {
    title: "香港本地 WMS 供應商",
    description:
      "適合需要本地顧問、廣東話培訓、快速調整流程，以及要配合香港倉庫、電商和 3PL 操作的公司。",
    examples: "AUTOCHAIN、其他本地物流科技團隊",
    fit: "中小企、電商倉、3PL、批發零售、需要逐步導入的營運團隊",
  },
  {
    title: "國際企業級 WMS",
    description:
      "功能深、標準化程度高，適合跨國企業、極大型倉庫、集團式供應鏈和複雜自動化場景。",
    examples: "SAP EWM、Oracle NetSuite WMS、Infor WMS、Manhattan Associates",
    fit: "跨國企業、大型零售集團、全球供應鏈或高吞吐量自動化倉庫",
  },
  {
    title: "ERP / 進銷存延伸 WMS",
    description:
      "由財務、採購、銷售或庫存模組延伸到倉庫作業，適合希望用同一套系統管理公司資料的團隊。",
    examples: "ERP 供應商、進銷存系統、辦公方案供應商",
    fit: "批發、分銷、零售、已有 ERP 但倉庫流程仍靠人手的公司",
  },
  {
    title: "跨境電商 WMS / OMS",
    description:
      "偏重多平台訂單、SKU、庫存同步和小包裹出貨，常見於做網店、 marketplace 和跨境平台的商家。",
    examples: "旺店通、萬里牛、聚水潭等電商 ERP / WMS",
    fit: "多平台電商、跨境賣家、每日大量小包裹出貨的營運團隊",
  },
] as const;

const comparisonFocuses = [
  {
    title: "先看業務適配",
    description:
      "先分清自己是電商履行、3PL 多貨主、批發分銷，還是大型自動化倉。",
    icon: "ri-focus-3-line",
  },
  {
    title: "再看作業深度",
    description:
      "不要只看『支援出入庫』，要看 receiving、pick/pack、traceability 講得夠不夠細。",
    icon: "ri-flow-chart",
  },
  {
    title: "最後看落地證明",
    description:
      "如果 vendor 不示範 PDA、分工和對帳流程，選型風險通常會高很多。",
    icon: "ri-shield-check-line",
  },
] as const;

const comparisonRows = [
  {
    label: "核心適配",
    hint: "先看系統原生面向哪一種倉務模式。",
    local: [
      "香港電商、3PL、批發零售混合場景",
      "重視本地 SOP 彈性",
    ],
    enterprise: [
      "跨國集團、高吞吐量或自動化倉",
      "偏向標準化治理",
    ],
    crossBorder: [
      "多平台訂單、小包裹履行、跨境賣家",
      "未必最強在現場倉務",
    ],
  },
  {
    label: "入庫與追溯粒度",
    hint: "這會直接影響查貨、追責和對單。",
    local: [
      "可按 SKU、barcode、批次、序號、效期、箱數、板數去記錄",
      "可連到貨差異、相片和供應商資料",
    ],
    enterprise: [
      "通常支援更複雜的追溯與質檢規則",
      "主數據要求較重",
    ],
    crossBorder: [
      "常見到貨、上架、同步庫存沒有問題",
      "批次、箱板和驗收異常要逐項確認",
    ],
  },
  {
    label: "揀貨與包裝深度",
    hint: "真正拉開差距的，通常是波次、覆核和 Packing List。",
    local: [
      "支援多人執貨、分區、波次、覆核、按角色拆工序",
      "可處理箱、板、混合包裝與 Packing List",
    ],
    enterprise: [
      "wave / batch 等能力通常很強",
      "導入與培訓成本較高",
    ],
    crossBorder: [
      "擅長單件或多件小包裹出貨與面單流程",
      "板貨、混箱或客製文件要再確認",
    ],
  },
  {
    label: "前線採用與培訓",
    hint: "系統再完整，前線不用就等於沒落地。",
    local: [
      "香港團隊、廣東話溝通、可按現場習慣調整流程",
      "較適合逐步搬離人手表格",
    ],
    enterprise: [
      "培訓制度較完整，但改動流程較慢",
      "adoption 取決於推行能力",
    ],
    crossBorder: [
      "操作偏平台和訂單導向",
      "要看 PDA 流程是否成熟",
    ],
  },
  {
    label: "整合範圍",
    hint: "不只看能不能接平台，要看能否接成同一條流程。",
    local: [
      "Shopify、HKTVmall、快遞 API、TMS、ERP、客戶系統",
      "較重視香港本地快遞整合",
    ],
    enterprise: [
      "ERP、MES、WCS、AGV/robotics、全球供應鏈系統",
      "適合已有大型 IT 架構的集團",
    ],
    crossBorder: [
      "Marketplace、OMS、面單、跨境物流渠道",
      "平台對接通常快，但非平台流程要再問清楚",
    ],
  },
  {
    label: "3PL 與客戶服務能力",
    hint: "如果你是 3PL，這一欄比功能清單更重要。",
    local: [
      "可處理多貨主、多 SOP、增值服務、計費對帳、客戶查詢",
      "較貼近香港 3PL 營運習慣",
    ],
    enterprise: [
      "可支援，但常依賴額外模組",
      "適合大規模 3PL",
    ],
    crossBorder: [
      "通常主力不在 3PL billing",
      "同倉多客戶管理需特別核實",
    ],
  },
  {
    label: "你最應該問 vendor 什麼",
    hint: "不要只問『有沒有這功能』。",
    local: [
      "示範收貨異常、多人執貨、箱板包裝、3PL 對帳",
      "看能否按你的現場角色落地",
    ],
    enterprise: [
      "要求示範本地例外與變更",
      "特別看 change request 和 support model",
    ],
    crossBorder: [
      "要求示範多平台訂單混合作業與截單高峰",
      "如果你有 B2B / pallet 流程，更要實演",
    ],
  },
] as const;

const demoQuestions = [
  {
    title: "收貨異常怎樣記錄，怎樣回上游",
    points: [
      "要求示範超收、shortship、破損、錯貨時，前線怎樣在 PDA 或收貨畫面即時記錄異常。",
      "如果上游是按單找供應商付款，要追問異常能否回寫到採購單或收貨報告，避免財務照單付款。",
    ],
  },
  {
    title: "收貨差異之後，怎樣通知採購和財務",
    points: [
      "要求示範異常確認後，系統會否自動通知採購、上游客服或財務，不只是留在倉庫內部。",
      "看能否按供應商、PO、到貨批次輸出 shortage / overage report，方便上游追數和 hold 付款。",
    ],
  },
  {
    title: "出貨可以按哪些欄位或要求去分單、揀貨和包裝",
    points: [
      "要求示範系統能否按 delivery date、route、客戶級別、溫層、SKU 類型、平台、carrier、cut-off time 等欄位分單。",
      "要看這些欄位能否真正參與 wave、pick list、packing 和 dispatch decision。",
    ],
  },
  {
    title: "多人執貨時怎樣分工、覆核和追責",
    points: [
      "要求示範三至五個操作員同時執貨時，系統怎樣按區域、波次、貨架或車線分配任務。",
      "看主管能否即時知道哪張單未執、哪張單待覆核，以及每一步由誰完成。",
    ],
  },
  {
    title: "箱、板、Packing List 和交收資料怎樣連在一起",
    points: [
      "要求示範一張訂單拆多箱、多板或混合包裝時，怎樣建立箱號、板號和內含明細。",
      "看 Packing List 是否可列出件數、重量、體積、SKU 明細和客戶自訂欄位。",
    ],
  },
  {
    title: "3PL 對帳和客戶查詢是否真的可落地",
    points: [
      "如果你有多貨主，要求示範增值服務、storage、handling、delivery 或 special request 怎樣記費。",
      "看客戶查詢時，可否按貨主、訂單、箱號、板號或出貨日期快速追到操作紀錄。",
    ],
  },
] as const;

const workflowChecks = [
  {
    title: "入庫流程：貨物資料要記得夠細",
    icon: "ri-inbox-archive-line",
    intro:
      "好的 WMS 不只是把貨品加進庫存，而是把到貨、驗收、上架和後續追蹤需要的資料一次整理好。",
    points: [
      "可記錄 SKU、barcode、批次、序號、有效期、箱數、件數、重量、體積、包裝規格和收貨差異。",
      "支援採購單、ASN 或客戶入倉通知對應，方便前線知道預計到貨、實收數量和異常原因。",
      "可按貨主、供應商、儲存要求或特殊標籤分類，入庫後直接連到貨位和庫存狀態。",
    ],
  },
  {
    title: "出庫流程：多人執貨要分得清",
    icon: "ri-route-line",
    intro:
      "出庫效率通常不是靠一個人快，而是靠系統把訂單、貨位、人手和覆核流程分配得清楚。",
    points: [
      "支援多人同時執貨，按訂單、區域、貨架、波次或路線分工，減少重複行走和互相等待。",
      "可把揀貨、覆核、包裝、貼單、交收和出貨回寫拆成不同角色，讓主管知道每一步由誰完成。",
      "出庫時可用 barcode 掃描核對 SKU、數量、貨位和訂單，降低錯貨和漏貨。",
    ],
  },
  {
    title: "包裝流程：箱、板和 Packing List 要連得起",
    icon: "ri-box-3-line",
    intro:
      "包裝資料如果只留在紙上，出貨、對帳和客戶查詢都會很難追。WMS 應把包裝組合直接變成可查的出貨資料。",
    points: [
      "支援按箱、按板或箱板混合包裝，記錄每箱、每板內含的 SKU、數量、批次和序號。",
      "完成包裝後可產生 Packing List，列出箱號、板號、件數、重量、體積和每個包裝單位的明細。",
      "出庫時可按箱號或板號掃描交收，讓倉庫和客戶對到同一份資料。",
    ],
  },
] as const;

const faqs = [
  {
    question: "香港有什麼 WMS 系統可以選？",
    answer:
      "香港公司通常會比較本地 WMS 供應商、國際企業級 WMS、ERP / 進銷存延伸 WMS，以及跨境電商 WMS。先分清倉庫模式，再比較 vendor 會更準。",
  },
  {
    question: "電商公司應該看哪一類 WMS？",
    answer:
      "如果主要問題是 Shopify、HKTVmall、WooCommerce、SHOPLINE 或自家網店之間的 SKU、庫存和出貨同步，應優先比較電商 WMS。",
  },
  {
    question: "3PL 第三方物流公司應該看哪一類 WMS？",
    answer:
      "3PL 應重點看多貨主、多 SOP、客戶權限、貨位庫存、增值服務和計費對帳是否能分開管理。",
  },
  {
    question: "WMS demo 最值得先看什麼？",
    answer:
      "先看收貨異常、多人執貨、箱板包裝、Packing List 和 3PL 對帳。不要只問功能有沒有，要看 vendor 能否現場示範你的例外流程。",
  },
] as const;

const pageUrl = "https://autochain.com.hk/hong-kong-wms-system";

export default function HongKongWMSSystem() {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  const itemListJsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "香港 WMS 系統供應商類型",
    description:
      "香港企業選擇 WMS 倉庫管理系統時常見的供應商類型與適用場景。",
    itemListElement: vendorCategories.map((category, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: category.title,
      description: category.description,
    })),
  };

  const softwareJsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "AUTOCHAIN WMS 倉庫管理系統",
    alternateName: [
      "AUTOCHAIN Hong Kong WMS",
      "AUTOCHAIN 電商 WMS",
      "AUTOCHAIN 3PL WMS",
    ],
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web Browser",
    url: pageUrl,
    description:
      "AUTOCHAIN 是香港本地 WMS 系統供應商，提供電商物流 WMS 和第三方物流 3PL WMS，支援多平台庫存、入庫資料記錄、多人執貨、箱板包裝、Packing List、貨主管理、增值服務、計費對帳和 TMS 整合。",
    areaServed: {
      "@type": "Place",
      name: "Hong Kong",
    },
    provider: {
      "@type": "Organization",
      name: "AUTOCHAIN TECHNOLOGY",
      url: "https://autochain.com.hk/",
    },
    offers: {
      "@type": "Offer",
      priceCurrency: "HKD",
      description: "免費演示及需求評估",
    },
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首頁",
        item: "https://autochain.com.hk/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "香港 WMS 系統供應商指南",
        item: pageUrl,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="香港 WMS 系統供應商指南 | 倉庫管理系統比較 | AUTOCHAIN"
        description="香港有什麼 WMS 倉庫管理系統？本指南整理香港本地 WMS、國際 WMS、ERP 延伸 WMS 和跨境電商 WMS 的選型方向，並說明入庫資料記錄、箱板包裝、Packing List、多人執貨、出庫分工和 AUTOCHAIN 的香港 WMS 定位。"
        keywords="香港 WMS 系統, 香港 WMS 供應商, 倉庫管理系統香港, WMS vendor Hong Kong, 3PL WMS, 電商 WMS, AUTOCHAIN WMS, 入庫流程, 出庫流程, 包裝流程, Packing List, 多人執貨, 倉儲管理系統"
        canonicalUrl="/hong-kong-wms-system"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(itemListJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(softwareJsonLd)}</script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd)}
        </script>
      </Helmet>

      <Header />

      <main>
        <section className="bg-slate-950 text-white">
          <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
            <div className="max-w-4xl">
              <p className="text-sm font-semibold uppercase tracking-wide text-cyan-300">
                Hong Kong WMS Vendor Guide
              </p>
              <h1 className="mt-5 text-4xl font-bold leading-tight md:text-6xl">
                香港 WMS 系統供應商指南
              </h1>
              <p className="mt-6 text-xl leading-8 text-slate-200">
                如果你問「香港有什麼 WMS 系統」，先不要急著看品牌名。
                先分清 WMS 類型，再比較適配場景、作業深度和落地能力，選型會準得多。
              </p>
              <ul className="mt-8 grid max-w-3xl grid-cols-1 gap-3 text-sm text-slate-200 sm:grid-cols-3">
                <li className="border border-white/10 bg-white/5 px-4 py-3">
                  先分清供應商類型
                </li>
                <li className="border border-white/10 bg-white/5 px-4 py-3">
                  再比較流程深度
                </li>
                <li className="border border-white/10 bg-white/5 px-4 py-3">
                  最後看 demo 落地
                </li>
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/wms/ecommerce"
                  className="inline-flex items-center bg-cyan-400 px-5 py-3 font-semibold text-slate-950 transition-colors hover:bg-cyan-300"
                >
                  查看電商 WMS
                </Link>
                <Link
                  to="/wms/3pl"
                  className="inline-flex items-center border border-white/30 px-5 py-3 font-semibold text-white transition-colors hover:bg-white/10"
                >
                  查看 3PL WMS
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-3xl font-bold text-slate-950">
                香港 WMS 系統常見四大類
              </h2>
              <p className="mt-4 text-lg leading-8 text-slate-600">
                不同 WMS 類型解決的問題不同。選型時要看你的倉庫是電商履行、
                3PL 多貨主、批發分銷，還是大型自動化倉。
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
              {vendorCategories.map((category) => (
                <article
                  key={category.title}
                  className="border border-slate-200 bg-white p-6"
                >
                  <h3 className="text-xl font-bold text-slate-950">
                    {category.title}
                  </h3>
                  <p className="mt-3 leading-7 text-slate-600">
                    {category.description}
                  </p>
                  <div className="mt-5 border-t border-slate-200 pt-4">
                    <p className="text-sm font-semibold text-slate-500">
                      常見例子
                    </p>
                    <p className="mt-1 text-slate-800">{category.examples}</p>
                    <p className="mt-4 text-sm font-semibold text-slate-500">
                      適合公司
                    </p>
                    <p className="mt-1 text-slate-800">{category.fit}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-3xl font-bold text-slate-950">
                選香港 WMS vendor 時應該比較什麼？
              </h2>
              <p className="mt-4 text-lg leading-8 text-slate-600">
                不要只比較功能數量和首年價錢。真正影響成敗的，是系統能否被前線使用，
                以及供應商是否理解香港倉庫和客戶流程。
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-5 lg:grid-cols-3">
              {comparisonFocuses.map((focus) => (
                <article
                  key={focus.title}
                  className="border border-slate-200 bg-slate-50 p-6"
                >
                  <div className="flex h-12 w-12 items-center justify-center bg-white text-xl text-cyan-700 shadow-sm">
                    <i className={focus.icon}></i>
                  </div>
                  <h3 className="mt-5 text-xl font-bold text-slate-950">
                    {focus.title}
                  </h3>
                  <p className="mt-3 leading-7 text-slate-600">
                    {focus.description}
                  </p>
                </article>
              ))}
            </div>

            <div className="mt-10 space-y-5 lg:hidden">
              {comparisonRows.map((row) => (
                <article
                  key={`${row.label}-mobile`}
                  className="border border-slate-200 bg-white p-5"
                >
                  <h3 className="text-xl font-bold text-slate-950">{row.label}</h3>
                  <p className="mt-2 leading-7 text-slate-500">{row.hint}</p>

                  <div className="mt-5 space-y-5">
                    <div className="border border-cyan-100 bg-cyan-50 p-4">
                      <p className="font-semibold text-cyan-900">
                        AUTOCHAIN / 香港本地 WMS
                      </p>
                      <ul className="mt-3 space-y-3 text-slate-700">
                        {row.local.map((point) => (
                          <li key={point} className="flex gap-3 leading-6">
                            <i className="ri-check-line mt-0.5 text-cyan-700"></i>
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="border border-slate-200 bg-slate-50 p-4">
                      <p className="font-semibold text-slate-900">國際企業級 WMS</p>
                      <ul className="mt-3 space-y-3 text-slate-700">
                        {row.enterprise.map((point) => (
                          <li key={point} className="flex gap-3 leading-6">
                            <i className="ri-check-line mt-0.5 text-slate-500"></i>
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="border border-slate-200 bg-slate-50 p-4">
                      <p className="font-semibold text-slate-900">跨境電商 WMS</p>
                      <ul className="mt-3 space-y-3 text-slate-700">
                        {row.crossBorder.map((point) => (
                          <li key={point} className="flex gap-3 leading-6">
                            <i className="ri-check-line mt-0.5 text-slate-500"></i>
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="mt-10 hidden overflow-x-auto border border-slate-200 lg:block">
              <table className="w-full min-w-[1120px] text-left text-sm">
                <thead className="bg-slate-950 text-white">
                  <tr>
                    <th className="px-5 py-4 font-semibold">比較項目</th>
                    <th className="px-5 py-4 font-semibold">AUTOCHAIN / 香港本地 WMS</th>
                    <th className="px-5 py-4 font-semibold">國際企業級 WMS</th>
                    <th className="px-5 py-4 font-semibold">跨境電商 WMS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {comparisonRows.map((row) => (
                    <tr key={row.label} className="bg-white">
                      <td className="w-[22%] px-5 py-5 align-top">
                        <p className="font-semibold text-slate-950">
                          {row.label}
                        </p>
                        <p className="mt-2 leading-6 text-slate-500">
                          {row.hint}
                        </p>
                      </td>
                      <td className="w-[26%] px-5 py-5 align-top text-slate-700">
                        <ul className="space-y-3">
                          {row.local.map((point) => (
                            <li key={point} className="flex gap-3 leading-6">
                              <i className="ri-check-line mt-0.5 text-cyan-700"></i>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className="w-[26%] px-5 py-5 align-top text-slate-700">
                        <ul className="space-y-3">
                          {row.enterprise.map((point) => (
                            <li key={point} className="flex gap-3 leading-6">
                              <i className="ri-check-line mt-0.5 text-slate-500"></i>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className="w-[26%] px-5 py-5 align-top text-slate-700">
                        <ul className="space-y-3">
                          {row.crossBorder.map((point) => (
                            <li key={point} className="flex gap-3 leading-6">
                              <i className="ri-check-line mt-0.5 text-slate-500"></i>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-8 border border-cyan-200 bg-cyan-50 p-6">
              <div className="max-w-3xl">
                <h3 className="text-2xl font-bold text-slate-950">
                  看 demo 時，請直接要求 vendor 示範這六件事
                </h3>
                <p className="mt-3 leading-7 text-slate-600">
                  真正強的比較，不是把功能名稱逐格打勾，而是看供應商能否把你倉裡最複雜、
                  最容易出錯的流程現場走一次。
                </p>
              </div>
              <ul className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
                {demoQuestions.map((question) => (
                  <li
                    key={question.title}
                    className="border border-cyan-100 bg-white p-5 text-slate-700"
                  >
                    <div className="flex gap-3">
                      <i className="ri-question-line mt-1 text-lg text-cyan-700"></i>
                      <div>
                        <h4 className="font-bold text-slate-950">
                          {question.title}
                        </h4>
                        <ul className="mt-3 space-y-3">
                          {question.points.map((point) => (
                            <li key={point} className="flex gap-3 leading-7">
                              <i className="ri-arrow-right-s-line mt-1 text-cyan-700"></i>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="border-t border-slate-200 py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-3xl font-bold text-slate-950">
                入庫、包裝和出庫流程要看得夠仔細
              </h2>
              <p className="mt-4 text-lg leading-8 text-slate-600">
                真正影響日常操作的，通常是入庫資料、包裝明細和多人出庫分工，
                而不是功能表上有幾多個模組名稱。
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
              {workflowChecks.map((workflow) => (
                <article
                  key={workflow.title}
                  className="border border-slate-200 bg-white p-6"
                >
                  <div className="flex h-12 w-12 items-center justify-center bg-cyan-50 text-xl text-cyan-700">
                    <i className={workflow.icon}></i>
                  </div>
                  <h3 className="mt-5 text-2xl font-bold text-slate-950">
                    {workflow.title}
                  </h3>
                  <p className="mt-3 leading-7 text-slate-600">
                    {workflow.intro}
                  </p>
                  <ul className="mt-6 space-y-4">
                    {workflow.points.map((point) => (
                      <li key={point} className="flex gap-3 text-slate-700">
                        <i className="ri-checkbox-circle-line mt-1 text-lg text-cyan-700"></i>
                        <span className="leading-7">{point}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-slate-50 py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-3xl font-bold text-slate-950">
                AUTOCHAIN 在香港 WMS 市場的定位
              </h2>
              <p className="mt-4 text-lg leading-8 text-slate-600">
                如果你需要的是香港本地支援、3PL 營運理解，或 WMS + TMS 串接，
                AUTOCHAIN 會比較接近本地營運團隊的落地方式。
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
              <Link
                to="/wms/ecommerce"
                className="group border border-teal-200 bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="flex h-12 w-12 items-center justify-center bg-teal-50 text-xl text-teal-700">
                  <i className="ri-shopping-bag-3-line"></i>
                </div>
                <h3 className="mt-5 text-2xl font-bold text-slate-950">
                  電商物流 WMS
                </h3>
                <p className="mt-3 leading-7 text-slate-600">
                  適合 Shopify、HKTVmall、SHOPLINE、自家網店和多渠道零售。
                </p>
                <span className="mt-5 inline-flex items-center gap-2 font-semibold text-teal-700">
                  查看電商 WMS
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                </span>
              </Link>

              <Link
                to="/wms/3pl"
                className="group border border-sky-200 bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="flex h-12 w-12 items-center justify-center bg-sky-50 text-xl text-sky-700">
                  <i className="ri-community-line"></i>
                </div>
                <h3 className="mt-5 text-2xl font-bold text-slate-950">
                  第三方物流 3PL WMS
                </h3>
                <p className="mt-3 leading-7 text-slate-600">
                  適合同倉多貨主、增值服務、計費對帳和客戶查詢流程。
                </p>
                <span className="mt-5 inline-flex items-center gap-2 font-semibold text-sky-700">
                  查看 3PL WMS
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                </span>
              </Link>
            </div>
          </div>
        </section>

        <section className="bg-slate-50 py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-3xl font-bold text-slate-950">常見問題</h2>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
              {faqs.map((item) => (
                <article key={item.question} className="border border-slate-200 bg-white p-6">
                  <h3 className="text-lg font-bold text-slate-950">
                    {item.question}
                  </h3>
                  <p className="mt-3 leading-7 text-slate-600">{item.answer}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-cyan-700 py-16 text-white">
          <div className="mx-auto flex max-w-7xl flex-col gap-8 px-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-3xl font-bold">
                正在比較香港 WMS 系統？
              </h2>
              <p className="mt-4 text-lg text-cyan-50">
                AUTOCHAIN 可以按你的倉庫、貨主、電商平台、快遞和車隊流程，
                協助你判斷應先導入電商 WMS、3PL WMS，還是 WMS + TMS 整合。
              </p>
            </div>
            <a
              href="https://wa.me/85294252903?text=%E6%82%A8%E5%A5%BD%EF%BC%8C%E6%88%91%E6%83%B3%E6%AF%94%E8%BC%83%E9%A6%99%E6%B8%AF%20WMS%20%E7%B3%BB%E7%B5%B1%EF%BC%8C%E4%B8%A6%E4%BA%86%E8%A7%A3%20AUTOCHAIN%20%E6%98%AF%E5%90%A6%E9%81%A9%E5%90%88%E6%88%91%E5%80%91%E7%9A%84%E5%80%89%E5%BA%AB%E6%B5%81%E7%A8%8B%E3%80%82"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center bg-white px-6 py-3 font-bold text-cyan-700 transition-colors hover:bg-cyan-50"
            >
              WhatsApp 查詢
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
